<template>
  <div
    id="assistant-step-2"
    class="assistant-forms" >
    <h2 class="title">Role and communication style</h2>
    <p class="subtitle">Select your AI assistant's role and customize its communication style.</p>

    <JsonSchema
      ref="jsonSchema"
      v-model="value"
      :schema="schema | processSchema"
      :components="componentsConfig"
      :ui-schema="uiSchema"
      :wrapper="wrapperComponentConfig"
      @init-default="initDefault"
      @input="() => {}" />

    <!-- <div> dataModel </div> -->
    <!-- <pre class="debug"> {{ value }} </pre> -->
  </div>
</template>

<script>
// TODO - check this code before publishing Assistants feature

import JsonSchema from '@roma219/vue-jsonschema-form';
import { debounce } from 'debounce';

import SBIconList from '@/helpers/lists/sb-icon-list';
import ToasterMixin from '../../../mixins/Toaster';

/**
 * Filters the object and returns the part of the object at the path provided.
 * e.g. 'firstChild.secondChild.thirdChild' will return full copy of object.firstChild.secondChild.thirdChild value.
 * If the path is not valid it will return null.
 *
 * @param {Object} object - Object to filter.
 * @param {String} path - Path to the object part that needs to be returned.
 */
function getObjectCopyByPath(object, path) {
  let objectCopy = JSON.parse(JSON.stringify(object));
  path = path.split('.');
  try {
    path.forEach((step) => {
      objectCopy = objectCopy[step];
    });
  } catch (e) {
    return null;
  }
  return objectCopy;
}

const schemaBP = {
  type: 'object',
  properties: {
    role: {
      value: 1,
      type: 'dropdown',
      title: 'Role',
      validators: 'required',
      options: [
        { value: 1, label: 'Customer support', color: '#f8ffff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg' },
        { value: 2, label: 'Sales', color: '#fff8f8', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
        { value: 3, label: 'Lead qualification', color: '#faf8ff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_always_gray.svg' },
        { value: 4, label: 'Custom', color: '#f8ffff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg' },
      ],
    },
    roleForm: {
      showIf: {
        input: 'role',
        value: [1],
      },
      type: 'object',
      child: true,
      properties: {
        // question: {
        //   type: 'input',
        //   title: 'When a contact asks any question',
        //   value: 'Our products and services',
        //   placeholder: 'My question is...',
        //   validators: 'required',
        // },
        response: {
          type: 'dropdown',
          title: 'When a contact asks any question',
          validators: 'required',
          options: [
            { value: 1, label: 'Look for an answer in the provided data' },
            // { value: 2, label: 'Transfer to a human agent' },
            // { value: 3, label: 'Ask for contact details' },
          ],
        },
        urlInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'url-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
          addButtonText: 'Connect website',
          populatedText: ['url', 'pathList'],
          minUrls: 0,
          maxUrls: 3,
          returnKey: 'providedUrls',
          validOneOf: ['fileInput', 'urlInput'],
          validOneOfMessage: 'Add at least one data source.',
          countFields: ['includePaths', 'excludePaths1'], ///// WATCH THIS FOR DOCUMENTATION - use if modal is available
          modal: {
            title: 'Connect website',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
            schema: {
              type: 'object',
              properties: {
                radio: {
                  value: 1,
                  type: 'radio-NMBR',
                  validators: 'required',
                  options: [
                    { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    // { value: 2, label: 'Crawl', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' }, ////// TODO enable more than two options in one row
                  ],
                },
                divider: {
                  type: 'divider',
                },
                url: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'input',
                  title: 'Website URL',
                  placeholder: 'https://website.com',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Crawl all available pages starting with this URL.',
                  validators: 'url',
                  hideOptional: true,
                },
                title: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'group-title',
                  title: 'Filter options',
                },
                includePaths: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Include only specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., products/* or products/product-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add', // newLine, add
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'includePaths',
                  bottomSpacer: 3,
                },
                excludePaths1: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Exclude specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., blog/* or blog/article-name.html',
                  validators: 'urlPath',
                  enter: 'Add',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'excludePaths',
                  bottomSpacer: 3,
                },
                pathList: {
                  showIf: {
                    input: 'radio',
                    value: 2,
                  },
                  type: 'message-buttons',
                  title: 'List of URLs',
                  subtitle: 'Only links you specify here will be crawled.',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add link',
                  placeholder: 'https://website.com/about.html',
                  validators: 'required|url',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Url is required.',
                    // url: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add', // newLine, add
                  minButtons: 1,
                  maxButtons: 100,
                  returnKey: 'urlList',
                },
                divider2: {
                  type: 'divider',
                },
                description: {
                  type: 'dropdown',
                  title: 'Rescrape pages',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Define how often added pages should be rescraped.',
                  validators: 'required',
                  value: 4,
                  options: [
                    { value: 1, label: 'Never' },
                    { value: 2, label: 'Every day' },
                    { value: 3, label: 'Every week' },
                    { value: 4, label: 'Every month' },
                  ],
                },
              },
            },
          },
        },
        fileInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'file-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
          // addButtonIcon: 'https://flagcdn.com/us.svg',
          addButtonText: 'Add files...',
          allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
          minFiles: 0,
          maxFiles: 1000,
          validOneOf: ['fileInput', 'urlInput'],
          returnKey: 'providedFiles',
        },
        skeleton: {
          type: 'skeleton',
          title: 'And then respond in the following format',
          placeholder: 'Message created based on the provided data…',
        },
        buttons: {
          value: [
            { title: '👍 That was helpful' },
            { title: 'Continue chat' },
          ],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          buttonMaxTextLength: 25,
          minButtons: 0,
          maxButtons: 10,
          enter: 'Add',
          returnKey: 'responseButtons',
          child: true,
          validators: 'required|maxLength:20',
          validatorMessages: {
            required: 'Button text is required.',          // Set this to prevent unwanted default messages
            maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
          },
        },
      },
    },
    roleForm2: {
      showIf: {
        input: 'role',
        value: [2],
      },
      type: 'object',
      child: true,
      properties: {
        // question: {
        //   type: 'input',
        //   title: 'When a contact asks any question',
        //   value: 'Our products and services',
        //   placeholder: 'My question is...',
        //   validators: 'required',
        // },
        response: {
          type: 'dropdown',
          title: 'When a contact asks any question',
          validators: 'required',
          options: [
            { value: 1, label: 'Look for an answer in the provided data' },
            // { value: 2, label: 'Transfer to a human agent' },
            // { value: 3, label: 'Ask for contact details' },
          ],
        },
        urlInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'url-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
          addButtonText: 'Connect website',
          populatedText: ['url', 'pathList'],
          minUrls: 0,
          maxUrls: 3,
          validOneOf: ['fileInput', 'urlInput'],
          validOneOfMessage: 'Add at least one data source.',
          returnKey: 'providedUrls',
          countFields: ['includePaths', 'excludePaths1'], ///// WATCH THIS FOR DOCUMENTATION - use if modal is available
          modal: {
            title: 'Connect website',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
            schema: {
              type: 'object',
              properties: {
                radio: {
                  value: 1,
                  type: 'radio-NMBR',
                  validators: 'required',
                  options: [
                    { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    // { value: 2, label: 'Crawl', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' }, ////// TODO enable more than two options in one row
                  ],
                },
                divider: {
                  type: 'divider',
                },
                url: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'input',
                  title: 'Website URL',
                  placeholder: 'https://website.com',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Crawl all available pages starting with this URL.',
                  validators: 'url',
                  hideOptional: true,
                },
                title: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'group-title',
                  title: 'Filter options',
                },
                includePaths: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Include only specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., products/* or products/product-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',                    // newLine, add
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'includePaths',
                  bottomSpacer: 3,
                },
                excludePaths1: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Exclude specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., blog/* or blog/article-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'excludePaths',
                  bottomSpacer: 3,
                },
                pathList: {
                  showIf: {
                    input: 'radio',
                    value: 2,
                  },
                  type: 'message-buttons',
                  title: 'List of URLs',
                  subtitle: 'Only links you specify here will be crawled.',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add link',
                  placeholder: 'https://website.com/about.html',
                  validators: 'required|url',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Url is required.',
                    // url: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add', // newLine, add
                  minButtons: 1,
                  maxButtons: 100,
                  returnKey: 'urlList',
                },
                divider2: {
                  type: 'divider',
                },
                description: {
                  type: 'dropdown',
                  title: 'Rescrape pages',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Define how often added pages should be rescraped.',
                  validators: 'required',
                  value: 4,
                  options: [
                    { value: 1, label: 'Never' },
                    { value: 2, label: 'Every day' },
                    { value: 3, label: 'Every week' },
                    { value: 4, label: 'Every month' },
                  ],
                },
              },
            },
          },
        },
        fileInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'file-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
          // addButtonIcon: 'https://flagcdn.com/us.svg',
          addButtonText: 'Add files...',
          allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
          minFiles: 0,
          maxFiles: 1000,
          validOneOf: ['fileInput', 'urlInput'],
          returnKey: 'providedFiles',
        },
        skeleton: {
          type: 'skeleton',
          title: 'And then respond in the following format',
          placeholder: 'Message created based on the provided data…',
        },
        buttons: {
          value: [],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          buttonMaxTextLength: 25,
          minButtons: 0,
          maxButtons: 10,
          returnKey: 'responseButtons',
          child: true,
          enter: 'Add',
          validators: 'required|maxLength:20',
          validatorMessages: {
            required: 'Button text is required.',          // Set this to prevent unwanted default messages
            maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
          },
        },
        divider: {
          type: 'dividerFull',
        },
        userWants: {
          type: 'input',
          value: 'Purchase a product or service',
          title: 'When a contact wants to',
          placeholder: 'Purchase a product or service',
          errorMessage: 'Schema error message',
          validatorMessages: {
            required: 'Field is required.',
          },
          validators: 'required|minLength:20|maxLength:100',
          warningValidators: 'minLength',
        },
        role: {
          value: 1,
          type: 'dropdown',
          title: 'Role',
          validators: 'required',
          options: [
            { value: 1, label: 'Collect info from the contact' },
          ],
        },
        message: {
          type: 'textarea',
          title: '',
          placeholder: 'Product or service name, full name, and address.',
          validators: 'maxLength:1024',
          child: true,
        },
      },
    },
    roleForm3: {
      showIf: {
        input: 'role',
        value: [3],
      },
      type: 'object',
      child: true,
      properties: {
        response: {
          type: 'dropdown',
          title: 'When a contact asks any question',
          validators: 'required',
          options: [
            { value: 1, label: 'Look for an answer in the provided data' },
            // { value: 2, label: 'Transfer to a human agent' },
            // { value: 3, label: 'Ask for contact details' },
          ],
        },
        urlInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'url-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
          addButtonText: 'Connect website',
          populatedText: ['url', 'pathList'],
          minUrls: 0,
          maxUrls: 3,
          validOneOf: ['fileInput', 'urlInput'],
          validOneOfMessage: 'Add at least one data source.',
          returnKey: 'providedUrls',
          countFields: ['includePaths', 'excludePaths1'], ///// WATCH THIS FOR DOCUMENTATION - use if modal is available
          modal: {
            title: 'Connect website',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
            schema: {
              type: 'object',
              properties: {
                radio: {
                  value: 1,
                  type: 'radio-NMBR',
                  validators: 'required',
                  options: [
                    { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    // { value: 2, label: 'Crawl', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' }, ////// TODO enable more than two options in one row
                  ],
                },
                divider: {
                  type: 'divider',
                },
                url: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'input',
                  title: 'Website URL',
                  placeholder: 'https://website.com',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Crawl all available pages starting with this URL.',
                  validators: 'url',
                  hideOptional: true,
                },
                title: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'group-title',
                  title: 'Filter options',
                },
                includePaths: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Include only specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., products/* or products/product-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',                    // newLine, add
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'includePaths',
                  bottomSpacer: 3,
                },
                excludePaths1: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Exclude specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., blog/* or blog/article-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'excludePaths',
                  bottomSpacer: 3,
                },
                pathList: {
                  showIf: {
                    input: 'radio',
                    value: 2,
                  },
                  type: 'message-buttons',
                  title: 'List of URLs',
                  subtitle: 'Only links you specify here will be crawled.',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add link',
                  placeholder: 'https://website.com/about.html',
                  validators: 'required|url',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Url is required.',
                    // url: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add', // newLine, add
                  minButtons: 1,
                  maxButtons: 100,
                  returnKey: 'urlList',
                },
                divider2: {
                  type: 'divider',
                },
                description: {
                  type: 'dropdown',
                  title: 'Rescrape pages',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Define how often added pages should be rescraped.',
                  validators: 'required',
                  value: 4,
                  options: [
                    { value: 1, label: 'Never' },
                    { value: 2, label: 'Every day' },
                    { value: 3, label: 'Every week' },
                    { value: 4, label: 'Every month' },
                  ],
                },
              },
            },
          },
        },
        fileInput: {
          showIf: {
            input: 'response',
            value: 1,
          },
          child: true,
          width: 50,
          value: [],
          type: 'file-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
          // addButtonIcon: 'https://flagcdn.com/us.svg',
          addButtonText: 'Add files...',
          allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
          minFiles: 0,
          maxFiles: 1000,
          validOneOf: ['fileInput', 'urlInput'],
          returnKey: 'providedFiles',
        },
        skeleton: {
          type: 'skeleton',
          title: 'And then respond in the following format',
          placeholder: 'Message created based on the provided data…',
        },
        buttons: {
          value: [],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          buttonMaxTextLength: 25,
          minButtons: 0,
          maxButtons: 10,
          returnKey: 'responseButtons',
          child: true,
          enter: 'Add',
          validators: 'required|maxLength:20',
          validatorMessages: {
            required: 'Button text is required.',          // Set this to prevent unwanted default messages
            maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
          },
        },
        divider: {
          type: 'dividerFull',
        },
        question: {
          type: 'input',
          title: 'When a contact wants to',
          value: 'Purchase a product or service',
          placeholder: 'Purchase a product or service',
          validators: 'required|minLength:20|maxLength:100',
          warningValidators: 'minLength',
        },
        then: {
          type: 'dropdown',
          title: 'Then',
          validators: 'required',
          options: [
            { value: 1, label: 'Collect info by asking questions' },
          ],
        },
        questions: {
          type: 'questions',
          // value: ['test 1', 'test 2'],
          addQuestionIcon: SBIconList.PLUS,
          addQuestionText: 'Add question',
          minQuestions: 1,
          maxQuestions: 10,
          minDefaultQuestions: 1,
          placeholder: 'What is your full name and address?',
          // enter: 'Add',
          child: true,
          returnKey: 'questionsList',
          validators: 'required|maxLength:100',
          validatorMessages: {
            required: 'Question text is required.',          // Set this to prevent unwanted default messages
          },
        },
        divider2: {
          type: 'dividerFull',
        },
        additionalQuestions: {
          type: 'questions-list-modal',
          title: 'Additional conditional questions',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Add a group of questions that will be asked if previous answers met defined rule.',
          addQuestionIcon: SBIconList.PLUS,
          addQuestionText: 'Add group of questions',
          minQuestions: 0,
          maxQuestions: 10,
          minDefaultQuestions: 0,
          placeholder: 'What is your full name and address?',
          // enter: 'Add',
          returnKey: 'questionsList',
        },
      },
    },
    roleForm4: {
      showIf: {
        input: 'role',
        value: [4],
      },
      type: 'object',
      child: true,
      properties: {
        question: {
          type: 'input',
          title: 'Role',
          value: '',
          placeholder: 'E.g., Appointment scheduler',
          validators: 'required|minLength:20|maxLength:100',
          warningValidators: 'minLength',
        },
        title: {
          type: 'group-title',
          title: 'Knowledge base',
        },
        urlInput: {
          child: true,
          width: 50,
          value: [],
          type: 'url-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
          addButtonText: 'Connect website',
          populatedText: ['url', 'pathList'],
          minUrls: 0,
          maxUrls: 3,
          validOneOf: ['fileInput', 'urlInput'],
          validOneOfMessage: 'Add at least one data source.',
          returnKey: 'providedUrls',
          countFields: ['includePaths', 'excludePaths1'], ///// WATCH THIS FOR DOCUMENTATION - use if modal is available
          modal: {
            title: 'Connect website',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
            schema: {
              type: 'object',
              properties: {
                radio: {
                  value: 1,
                  type: 'radio-NMBR',
                  validators: 'required',
                  options: [
                    { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
                    // { value: 2, label: 'Crawl', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' }, ////// TODO enable more than two options in one row
                  ],
                },
                divider: {
                  type: 'divider',
                },
                url: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'input',
                  title: 'Website URL',
                  placeholder: 'https://website.com',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Crawl all available pages starting with this URL.',
                  validators: 'url',
                  hideOptional: true,
                },
                title: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'group-title',
                  title: 'Filter options',
                },
                includePaths: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Include only specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., products/* or products/product-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',                    // newLine, add
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'includePaths',
                  bottomSpacer: 3,
                },
                excludePaths1: {
                  showIf: {
                    input: 'radio',
                    value: 1,
                  },
                  type: 'message-buttons',
                  title: 'Exclude specific paths or pages',
                  titleType: 'normal',
                  subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add path or page',
                  placeholder: 'E.g., blog/* or blog/article-name.html',
                  validators: 'urlPath',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Path is required.',
                    minLength: 'Add descriptive info to improve the AI\'s responses.',
                    urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add',
                  minButtons: 1,
                  maxButtons: 10,
                  returnKey: 'excludePaths',
                  bottomSpacer: 3,
                },
                pathList: {
                  showIf: {
                    input: 'radio',
                    value: 2,
                  },
                  type: 'message-buttons',
                  title: 'List of URLs',
                  subtitle: 'Only links you specify here will be crawled.',
                  addButtonIcon: SBIconList.PLUS,
                  addButtonText: 'Add link',
                  placeholder: 'https://website.com/about.html',
                  validators: 'required|url',
                  warningValidators: '',
                  validatorMessages: {
                    required: 'Url is required.',
                    // url: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
                  },
                  enter: 'Add', // newLine, add
                  minButtons: 1,
                  maxButtons: 100,
                  returnKey: 'urlList',
                },
                divider2: {
                  type: 'divider',
                },
                description: {
                  type: 'dropdown',
                  title: 'Rescrape pages',
                  subtitlePrefixIcon: SBIconList.INFO,
                  subtitle: 'Define how often added pages should be rescraped.',
                  validators: 'required',
                  value: 4,
                  options: [
                    { value: 1, label: 'Never' },
                    { value: 2, label: 'Every day' },
                    { value: 3, label: 'Every week' },
                    { value: 4, label: 'Every month' },
                  ],
                },
              },
            },
          },
        },
        fileInput: {
          child: true,
          width: 50,
          value: [],
          type: 'file-input-wrapper',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
          // addButtonIcon: 'https://flagcdn.com/us.svg',
          addButtonText: 'Add files...',
          allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
          minFiles: 0,
          maxFiles: 1000,
          validOneOf: ['fileInput', 'urlInput'],
          returnKey: 'providedFiles',
        },
        // customValidator: {
        //   type: 'custom-validator',
        //   condition: 'one',   //  one, all
        //   check: ['fileInput', 'urlInput'],
        //   value: '.',
        //   message: 'Add at least one data source.',
        //   topSpacing: 'none', /////      WATCH FOR DOCUMENATION - none 0, small 8, normal 18
        // },
        message: {
          type: 'textarea',
          title: 'Custom instruction',
          height: 456,                       // Set custom height of input field
          placeholder: `E.g., Your goal is to make the process of booking, rescheduling, or canceling appointments as smooth and efficient as possible for the user. Here are the detailed instructions to guide you in fulfilling your role effectively:

1. Gather Appointment Details

Purpose of Appointment: Ask what the appointment is for (e.g., consultation, meeting, service, etc.).

Preferred Date and Time: Inquire about their preferred date(s) and time(s).

Availability Confirmation: Check for available slots and provide a few options if the user's preferred time is not available…`,
          validators: 'required|minLength:500|maxLength:5000',
          warningValidators: 'minLength',
        },
      },
    },
    success: {
      showIf: {
        input: 'role',
        value: [1],
      },
      color: 'default',
      type: 'object',
      title: '👍 When you conclude the contact was assisted',
      hideOptional: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message',
          value: 'I\'m glad I could help today. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          placeholder: 'I\'m glad I could help today. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          validators: 'required',
        },
        actionsTest: {
          value: [
            { value: 1 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
      },
    },
    success2: {
      showIf: {
        input: 'role',
        value: [2],
      },
      color: 'default',                  // if color is set then the showIf parent can't override it, and if set to default then the default one is used
      type: 'object',
      title: '👍 When you\'ve collected info from the contact',
      hideOptional: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message',
          value: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          placeholder: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          hideOptional: true,
          height: 70,
        },
        actionsTest: {
          value: [
            { value: 1 },
            { value: 3 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
      },
    },
    success3: {
      showIf: {
        input: 'role',
        value: [3],
      },
      color: 'default',                  // if color is set then the showIf parent can't override it, and if set to default then the default one is used
      type: 'object',
      title: '👍 When you\'ve collected info from the contact',
      hideOptional: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message',
          value: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          placeholder: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
          hideOptional: true,
        },
        actionsTest: {
          value: [
            { value: 1 },
            { value: 3 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
      },
    },
  },
};

// const pripremaZaBackend = {
//   type: 'object',
//   properties: {
//     role: {
//       value: 1,
//       type: 'dropdown',
//       title: 'Role',
//       validators: 'required',
//       options: [
//         { value: 1, label: 'Customer support', color: '#f8ffff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg' },
//         { value: 2, label: 'Sales', color: '#fff8f8', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//         { value: 3, label: 'Lead qualification', color: '#faf8ff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_always_gray.svg' },
//         { value: 4, label: 'Custom', color: '#f8ffff', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg' },
//       ],
//     },
//     roleForm: {
//       showIf: {
//         input: 'role',
//         value: [1],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         anyQuestion: {
//           type: 'dropdown',
//           title: 'When a contact asks any question',
//           validators: 'required',
//           options: [
//             { value: 1, label: 'Look for an answer in the provided data' },
//           ],
//         },
//         urlInput: {
//           showIf: {
//             input: 'anyQuestion',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'url-input-wrapper',
//           subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//           subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Connect website',
//           populatedText: ['url', 'pathList'],
//           returnKey: 'providedUrls',
//           validOneOf: ['fileInput', 'urlInput'],
//           validOneOfMessage: 'Add at least one data source.',
//           countFields: ['includePaths', 'excludePaths1'],
//           modal: {
//             title: 'Connect website',
//             subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//             subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
//             schema: {
//               type: 'object',
//               properties: {
//                 radio: {
//                   value: 1,
//                   type: 'radio-NMBR',
//                   validators: 'required',
//                   options: [
//                     { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                     { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                   ],
//                 },
//                 divider: {
//                   type: 'divider',
//                 },
//                 url: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'input',
//                   title: 'Website URL',
//                   placeholder: 'https://website.com',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Crawl all available pages starting with this URL.',
//                   validators: 'url',
//                   hideOptional: true,
//                 },
//                 title: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'group-title',
//                   title: 'Filter options',
//                 },
//                 includePaths: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Include only specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., products/* or products/product-name.html',
//                   validators: 'urlPath',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   enter: 'Add',
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'includePaths',
//                   bottomSpacer: 3,
//                 },
//                 excludePaths1: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Exclude specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., blog/* or blog/article-name.html',
//                   validators: 'urlPath',
//                   enter: 'Add',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'excludePaths',
//                   bottomSpacer: 3,
//                 },
//                 pathList: {
//                   showIf: {
//                     input: 'radio',
//                     value: 2,
//                   },
//                   type: 'message-buttons',
//                   title: 'List of URLs',
//                   subtitle: 'Only links you specify here will be crawled.',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add link',
//                   placeholder: 'https://website.com/about.html',
//                   validators: 'required|url',
//                   validatorMessages: {
//                     required: 'Url is required.',
//                   },
//                   enter: 'Add', // newLine, add
//                   minButtons: 1,
//                   maxButtons: 100,
//                   returnKey: 'urlList',
//                 },
//                 divider2: {
//                   type: 'divider',
//                 },
//                 rescrape: {
//                   type: 'dropdown',
//                   title: 'Rescrape pages',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Define how often added pages should be rescraped.',
//                   validators: 'required',
//                   value: 4,
//                   options: [
//                     { value: 1, label: 'Never' },
//                     { value: 2, label: 'Every day' },
//                     { value: 3, label: 'Every week' },
//                     { value: 4, label: 'Every month' },
//                   ],
//                 },
//               },
//             },
//           },
//         },
//         fileInput: {
//           showIf: {
//             input: 'response',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'file-input-wrapper',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Add files...',
//           allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
//           minFiles: 0,
//           maxFiles: 1000,
//           validOneOf: ['fileInput', 'urlInput'],
//           returnKey: 'providedFiles',
//         },
//         skeleton: {
//           type: 'skeleton',
//           title: 'And then respond in the following format',
//           placeholder: 'Message created based on the provided data…',
//         },
//         buttons: {
//           value: [
//             { title: '👍 That was helpful' },
//             { title: 'Continue chat' },
//           ],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           minButtons: 0,
//           maxButtons: 10,
//           enter: 'Add',
//           returnKey: 'responseButtons',
//           child: true,
//           validators: 'required|maxLength:20',
//           validatorMessages: {
//             required: 'Button text is required.',          // Set this to prevent unwanted default messages
//             maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
//           },
//         },
//       },
//     },
//     roleForm2: {
//       showIf: {
//         input: 'role',
//         value: [2],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         response: {
//           type: 'dropdown',
//           title: 'When a contact asks any question',
//           validators: 'required',
//           options: [
//             { value: 1, label: 'Look for an answer in the provided data' },
//           ],
//         },
//         urlInput: {
//           showIf: {
//             input: 'anyQuestion',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'url-input-wrapper',
//           subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//           subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Connect website',
//           populatedText: ['url', 'pathList'],
//           returnKey: 'providedUrls',
//           validOneOf: ['fileInput', 'urlInput'],
//           validOneOfMessage: 'Add at least one data source.',
//           countFields: ['includePaths', 'excludePaths1'],
//           modal: {
//             title: 'Connect website',
//             subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//             subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
//             schema: {
//               type: 'object',
//               properties: {
//                 radio: {
//                   value: 1,
//                   type: 'radio-NMBR',
//                   validators: 'required',
//                   options: [
//                     { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                     { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                   ],
//                 },
//                 divider: {
//                   type: 'divider',
//                 },
//                 url: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'input',
//                   title: 'Website URL',
//                   placeholder: 'https://website.com',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Crawl all available pages starting with this URL.',
//                   validators: 'url',
//                   hideOptional: true,
//                 },
//                 title: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'group-title',
//                   title: 'Filter options',
//                 },
//                 includePaths: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Include only specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., products/* or products/product-name.html',
//                   validators: 'urlPath',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   enter: 'Add',
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'includePaths',
//                   bottomSpacer: 3,
//                 },
//                 excludePaths1: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Exclude specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., blog/* or blog/article-name.html',
//                   validators: 'urlPath',
//                   enter: 'Add',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'excludePaths',
//                   bottomSpacer: 3,
//                 },
//                 pathList: {
//                   showIf: {
//                     input: 'radio',
//                     value: 2,
//                   },
//                   type: 'message-buttons',
//                   title: 'List of URLs',
//                   subtitle: 'Only links you specify here will be crawled.',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add link',
//                   placeholder: 'https://website.com/about.html',
//                   validators: 'required|url',
//                   validatorMessages: {
//                     required: 'Url is required.',
//                   },
//                   enter: 'Add', // newLine, add
//                   minButtons: 1,
//                   maxButtons: 100,
//                   returnKey: 'urlList',
//                 },
//                 divider2: {
//                   type: 'divider',
//                 },
//                 rescrape: {
//                   type: 'dropdown',
//                   title: 'Rescrape pages',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Define how often added pages should be rescraped.',
//                   validators: 'required',
//                   value: 4,
//                   options: [
//                     { value: 1, label: 'Never' },
//                     { value: 2, label: 'Every day' },
//                     { value: 3, label: 'Every week' },
//                     { value: 4, label: 'Every month' },
//                   ],
//                 },
//               },
//             },
//           },
//         },
//         fileInput: {
//           showIf: {
//             input: 'response',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'file-input-wrapper',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Add files...',
//           allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
//           minFiles: 0,
//           maxFiles: 1000,
//           validOneOf: ['fileInput', 'urlInput'],
//           returnKey: 'providedFiles',
//         },
//         skeleton: {
//           type: 'skeleton',
//           title: 'And then respond in the following format',
//           placeholder: 'Message created based on the provided data…',
//         },
//         buttons: {
//           value: [],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           minButtons: 0,
//           maxButtons: 10,
//           returnKey: 'responseButtons',
//           child: true,
//           enter: 'Add',
//           validators: 'required|maxLength:20',
//           validatorMessages: {
//             required: 'Button text is required.',          // Set this to prevent unwanted default messages
//             maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
//           },
//         },
//         divider: {
//           type: 'dividerFull',
//         },
//         userWants: {
//           type: 'input',
//           value: 'Purchase a product or service',
//           title: 'When a contact wants to',
//           placeholder: 'Purchase a product or service',
//           errorMessage: 'Schema error message',
//           validatorMessages: {
//             required: 'Field is required.',
//           },
//           validators: 'required|minLength:20|maxLength:100',
//           warningValidators: 'minLength',
//         },
//         role: {
//           value: 1,
//           type: 'dropdown',
//           title: 'Role',
//           validators: 'required',
//           options: [
//             { value: 1, label: 'Collect info from the contact' },
//           ],
//         },
//         message: {
//           type: 'textarea',
//           title: '',
//           placeholder: 'Product or service name, full name, and address.',
//           validators: 'maxLength:1024',
//           child: true,
//         },
//       },
//     },
//     roleForm3: {
//       showIf: {
//         input: 'role',
//         value: [3],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         response: {
//           type: 'dropdown',
//           title: 'When a contact asks any question',
//           validators: 'required',
//           options: [
//             { value: 1, label: 'Look for an answer in the provided data' },
//             // { value: 2, label: 'Transfer to a human agent' },
//             // { value: 3, label: 'Ask for contact details' },
//           ],
//         },
//         urlInput: {
//           showIf: {
//             input: 'anyQuestion',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'url-input-wrapper',
//           subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//           subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Connect website',
//           populatedText: ['url', 'pathList'],
//           returnKey: 'providedUrls',
//           validOneOf: ['fileInput', 'urlInput'],
//           validOneOfMessage: 'Add at least one data source.',
//           countFields: ['includePaths', 'excludePaths1'],
//           modal: {
//             title: 'Connect website',
//             subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//             subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
//             schema: {
//               type: 'object',
//               properties: {
//                 radio: {
//                   value: 1,
//                   type: 'radio-NMBR',
//                   validators: 'required',
//                   options: [
//                     { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                     { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                   ],
//                 },
//                 divider: {
//                   type: 'divider',
//                 },
//                 url: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'input',
//                   title: 'Website URL',
//                   placeholder: 'https://website.com',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Crawl all available pages starting with this URL.',
//                   validators: 'url',
//                   hideOptional: true,
//                 },
//                 title: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'group-title',
//                   title: 'Filter options',
//                 },
//                 includePaths: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Include only specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., products/* or products/product-name.html',
//                   validators: 'urlPath',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   enter: 'Add',
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'includePaths',
//                   bottomSpacer: 3,
//                 },
//                 excludePaths1: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Exclude specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., blog/* or blog/article-name.html',
//                   validators: 'urlPath',
//                   enter: 'Add',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'excludePaths',
//                   bottomSpacer: 3,
//                 },
//                 pathList: {
//                   showIf: {
//                     input: 'radio',
//                     value: 2,
//                   },
//                   type: 'message-buttons',
//                   title: 'List of URLs',
//                   subtitle: 'Only links you specify here will be crawled.',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add link',
//                   placeholder: 'https://website.com/about.html',
//                   validators: 'required|url',
//                   validatorMessages: {
//                     required: 'Url is required.',
//                   },
//                   enter: 'Add', // newLine, add
//                   minButtons: 1,
//                   maxButtons: 100,
//                   returnKey: 'urlList',
//                 },
//                 divider2: {
//                   type: 'divider',
//                 },
//                 rescrape: {
//                   type: 'dropdown',
//                   title: 'Rescrape pages',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Define how often added pages should be rescraped.',
//                   validators: 'required',
//                   value: 4,
//                   options: [
//                     { value: 1, label: 'Never' },
//                     { value: 2, label: 'Every day' },
//                     { value: 3, label: 'Every week' },
//                     { value: 4, label: 'Every month' },
//                   ],
//                 },
//               },
//             },
//           },
//         },
//         fileInput: {
//           showIf: {
//             input: 'response',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'file-input-wrapper',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Add files...',
//           allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
//           minFiles: 0,
//           maxFiles: 1000,
//           validOneOf: ['fileInput', 'urlInput'],
//           returnKey: 'providedFiles',
//         },
//         skeleton: {
//           type: 'skeleton',
//           title: 'And then respond in the following format',
//           placeholder: 'Message created based on the provided data…',
//         },
//         buttons: {
//           value: [],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           minButtons: 0,
//           maxButtons: 10,
//           returnKey: 'responseButtons',
//           child: true,
//           enter: 'Add',
//           validators: 'required|maxLength:20',
//           validatorMessages: {
//             required: 'Button text is required.',          // Set this to prevent unwanted default messages
//             maxLength: 'Please limit your text to {{validator}} characters.',      // Placeholders must be named as the validator name and vill be replaced with the value.
//           },
//         },
//         divider: {
//           type: 'dividerFull',
//         },
//         question: {
//           type: 'input',
//           title: 'When a contact wants to',
//           value: 'Purchase a product or service',
//           placeholder: 'Purchase a product or service',
//           validators: 'required|minLength:20|maxLength:100',
//           warningValidators: 'minLength',
//         },
//         then: {
//           type: 'dropdown',
//           title: 'Then',
//           validators: 'required',
//           options: [
//             { value: 1, label: 'Collect info by asking questions' },
//           ],
//         },
//         questions: {
//           type: 'questions',
//           // value: ['test 1', 'test 2'],
//           addQuestionIcon: SBIconList.PLUS,
//           addQuestionText: 'Add question',
//           minQuestions: 1,
//           maxQuestions: 10,
//           minDefaultQuestions: 1,
//           placeholder: 'E.g., What is your full name and address?',
//           // enter: 'Add',
//           child: true,
//           returnKey: 'questionsList',
//           validators: 'required|maxLength:100',
//           validatorMessages: {
//             required: 'Question text is required.',          // Set this to prevent unwanted default messages
//           },
//         },
//         divider2: {
//           type: 'dividerFull',
//         },
//         additionalQuestions: {
//           type: 'questions-list-modal',
//           title: 'Additional conditional questions',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'Add a group of questions that will be asked if previous answers met defined rule.',
//           addQuestionIcon: SBIconList.PLUS,
//           addQuestionText: 'Add group of questions',
//           minQuestions: 0,
//           maxQuestions: 10,
//           placeholder: 'E.g., What is your full name and address?',
//           returnKey: 'questionsList',
//         },
//       },
//     },
//     roleForm4: {
//       showIf: {
//         input: 'role',
//         value: [4],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         question: {
//           type: 'input',
//           title: 'Role',
//           value: '',
//           placeholder: 'E.g., Appointment scheduler',
//           validators: 'required|minLength:20|maxLength:100',
//           warningValidators: 'minLength',
//         },
//         title: {
//           type: 'group-title',
//           title: 'Knowledge base',
//         },
//         urlInput: {
//           showIf: {
//             input: 'anyQuestion',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'url-input-wrapper',
//           subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//           subtitle: 'Check web scraping tips <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Connect website',
//           populatedText: ['url', 'pathList'],
//           returnKey: 'providedUrls',
//           validOneOf: ['fileInput', 'urlInput'],
//           validOneOfMessage: 'Add at least one data source.',
//           countFields: ['includePaths', 'excludePaths1'],
//           modal: {
//             title: 'Connect website',
//             subtitlePrefixIcon: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg',
//             subtitle: 'Learn more about web scraping and get helpful tips <a href="http://www.google.com" target="_blank">here</a>.',
//             schema: {
//               type: 'object',
//               properties: {
//                 radio: {
//                   value: 1,
//                   type: 'radio-NMBR',
//                   validators: 'required',
//                   options: [
//                     { value: 1, label: 'Crawl entire website', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                     { value: 2, label: 'Crawl specific links', image: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg' },
//                   ],
//                 },
//                 divider: {
//                   type: 'divider',
//                 },
//                 url: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'input',
//                   title: 'Website URL',
//                   placeholder: 'https://website.com',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Crawl all available pages starting with this URL.',
//                   validators: 'url',
//                   hideOptional: true,
//                 },
//                 title: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'group-title',
//                   title: 'Filter options',
//                 },
//                 includePaths: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Include only specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Only paths or pages you specify here will be crawled (e.g., products/* for all pages under "products" or products/product-name.html for a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., products/* or products/product-name.html',
//                   validators: 'urlPath',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   enter: 'Add',
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'includePaths',
//                   bottomSpacer: 3,
//                 },
//                 excludePaths1: {
//                   showIf: {
//                     input: 'radio',
//                     value: 1,
//                   },
//                   type: 'message-buttons',
//                   title: 'Exclude specific paths or pages',
//                   titleType: 'normal',
//                   subtitle: 'Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under "blog" or blog/article-name.html to exclude a specific page).',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add path or page',
//                   placeholder: 'E.g., blog/* or blog/article-name.html',
//                   validators: 'urlPath',
//                   enter: 'Add',
//                   warningValidators: '',
//                   validatorMessages: {
//                     required: 'Path is required.',
//                     minLength: 'Add descriptive info to improve the AI\'s responses.',
//                     urlPath: 'Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html',
//                   },
//                   minButtons: 1,
//                   maxButtons: 10,
//                   returnKey: 'excludePaths',
//                   bottomSpacer: 3,
//                 },
//                 pathList: {
//                   showIf: {
//                     input: 'radio',
//                     value: 2,
//                   },
//                   type: 'message-buttons',
//                   title: 'List of URLs',
//                   subtitle: 'Only links you specify here will be crawled.',
//                   addButtonIcon: SBIconList.PLUS,
//                   addButtonText: 'Add link',
//                   placeholder: 'https://website.com/about.html',
//                   validators: 'required|url',
//                   validatorMessages: {
//                     required: 'Url is required.',
//                   },
//                   enter: 'Add', // newLine, add
//                   minButtons: 1,
//                   maxButtons: 100,
//                   returnKey: 'urlList',
//                 },
//                 divider2: {
//                   type: 'divider',
//                 },
//                 rescrape: {
//                   type: 'dropdown',
//                   title: 'Rescrape pages',
//                   subtitlePrefixIcon: SBIconList.INFO,
//                   subtitle: 'Define how often added pages should be rescraped.',
//                   validators: 'required',
//                   value: 4,
//                   options: [
//                     { value: 1, label: 'Never' },
//                     { value: 2, label: 'Every day' },
//                     { value: 3, label: 'Every week' },
//                     { value: 4, label: 'Every month' },
//                   ],
//                 },
//               },
//             },
//           },
//         },
//         fileInput: {
//           showIf: {
//             input: 'response',
//             value: 1,
//           },
//           child: true,
//           width: 50,
//           value: [],
//           type: 'file-input-wrapper',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'Check how to properly format files <a href="http://www.google.com" target="_blank">here</a>.',
//           addButtonText: 'Add files...',
//           allowedFileTypes: 'doc, docx, html, md, pdf, pptx, txt',
//           minFiles: 0,
//           maxFiles: 1000,
//           validOneOf: ['fileInput', 'urlInput'],
//           returnKey: 'providedFiles',
//         },
//         message: {
//           type: 'textarea',
//           title: 'Custom instruction',
//           height: 456,                       // Set custom height of input field
//           placeholder: "E.g., Your goal is to make the process of booking, rescheduling, or canceling appointments as smooth and efficient as possible for the user. Here are the detailed instructions to guide you in fulfilling your role effectively:

// 1. Gather Appointment Details

// Purpose of Appointment: Ask what the appointment is for (e.g., consultation, meeting, service, etc.).

// Preferred Date and Time: Inquire about their preferred date(s) and time(s).

// Availability Confirmation: Check for available slots and provide a few options if the user's preferred time is not available…",
//           validators: 'required|minLength:500|maxLength:5000',
//           warningValidators: 'minLength',
//         },
//       },
//     },
//     success: {
//       showIf: {
//         input: 'role',
//         value: [1],
//       },
//       color: 'default',
//       type: 'object',
//       title: '👍 When you conclude the contact was assisted',
//       hideOptional: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message',
//           value: 'I\'m glad I could help today. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           placeholder: 'I\'m glad I could help today. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           validators: 'required',
//         },
//         actionsTest: {
//           value: [
//             { value: 1 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//       },
//     },
//     success2: {
//       showIf: {
//         input: 'role',
//         value: [2],
//       },
//       color: 'default',                  // if color is set then the showIf parent can't override it, and if set to default then the default one is used
//       type: 'object',
//       title: '👍 When you\'ve collected info from the contact',
//       hideOptional: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message',
//           value: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           placeholder: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           hideOptional: true,
//           height: 70,
//         },
//         actionsTest: {
//           value: [
//             { value: 1 },
//             { value: 3 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//       },
//     },
//     success3: {
//       showIf: {
//         input: 'role',
//         value: [3],
//       },
//       color: 'default',                  // if color is set then the showIf parent can't override it, and if set to default then the default one is used
//       type: 'object',
//       title: '👍 When you\'ve collected info from the contact',
//       hideOptional: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message',
//           value: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           placeholder: 'Thanks for providing the information! Our sales team will contact you shortly to assist you further. We\'ll close the chat now, but feel free to reach out if you have more questions.',
//           hideOptional: true,
//         },
//         actionsTest: {
//           value: [
//             { value: 1 },
//             { value: 3 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//       },
//     },
//   },
// };

const image = require('@/assets/img/SB-Icons/two-chat-bubbles.svg'); // this is local image example.

export default {
  name: 'AssistantStep2',
  components: {
    JsonSchema,
  },
  mixins: [ToasterMixin],
  props: {
    // data: {
    //   type: String,
    //   default: '',
    // },
  },
  data() {
    return {
      initialized: false,
      steps: 4,
      currentStep: 1,
      initializedSteps: [],
      test7: false,
      customErrorMessage: {},
      // stepImage: 'https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg',
      stepImage: image,
      value: {},
      // showShema: false,
      // uiSchema: {
      //   bio: {
      //     'ui:options': {
      //       placeholder: 'Please enter your bio.',
      //       type: 'textarea',
      //       rows: 1,
      //     },
      //   },
      // },
      // uiSchema: {
      //   properties: {
      //     text: {
      //       uiType: 'b-input',
      //     },
      //   },
      // },
      schemaBlueprint: {
        type: 'object',
        properties: {
          test: {
            type: 'input',
            title: 'Test',
            placeholder: 'Test',
            validators: 'required',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Test subtitle.',
          },
          // ...(this.test7 ? {
          //   typeForm: {
          //     type: 'object',
          //     title: 'Blabla Condition',
          //     properties: {
          //       efghij: {
          //         type: 'input',
          //         title: 'Test',
          //         placeholder: 'Test',
          //         validators: 'required',
          //         subtitlePrefixIcon: SBIconList.INFO,
          //         subtitle: 'Test subtitle.',
          //       },
          //     },
          //   },
          // } : {}),
          typeForm: {
            showIf: {
              input: 'test',
              value: 'aaa',
            },
            type: 'object',
            title: 'Blabla Condition',
            properties: {
              efghij: {
                type: 'input',
                title: 'Test',
                placeholder: 'Test',
                validators: 'required',
                subtitlePrefixIcon: SBIconList.INFO,
                subtitle: 'Test subtitle.',
              },
            },
          },
          type: {
            value: 1,
            type: 'dropdown',
            title: 'Type of AI',
            placeholder: 'Select type of AI',
            validators: 'required',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'AI in type of {{ option }}.',
            options: [
              { value: 1, label: 'One', color: 'red' },
              { value: 2, label: 'Two' },
              { value: 3, label: 'Three', color: 'blue' },
            ],
            width: 50,
          },
          a: {
            type: 'input',
            title: 'Username',
          },
          b: {
            type: 'object',
            title: 'Blabla',
            properties: {
              e: {
                type: 'input',
                title: 'Is Admin',
              },
              f: {
                type: 'dropdown',
                title: 'Currency format',
                placeholder: 'USD ($100,00.00)',
                options: [
                  { value: 'USD', label: 'USD ($100,00.00)' },
                  { value: 'CAD', label: 'CAD (100,00.00$)' },
                  { value: 'HR', label: 'HR (100,00.00€)' },
                ],
                width: 50,
                subtitlePrefixIcon: SBIconList.INFO,
                subtitle: 'Select the format that AI will use in replies.',
              },
            },
          },
        },
        // if: {
        //   properties: {
        //     test: {
        //       const: 'aaa',
        //     },
        //   },
        // },
        // then: {
        //   properties: {
        //     typeForm: {
        //       show: true,
        //     },
        //   },
        // },
      },
      componentsConfig: [
        {
          componentName: 'BInputWrapper',
          uiSchemaMatcher: { uiType: 'BInputWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            // console.log('propName', propName);
            // console.log('schema', schema);
            // console.log('uiSchema', uiSchema);
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'SBTextareaWrapper',
          uiSchemaMatcher: { uiType: 'SBTextareaWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'SBDropdownWrapper',
          uiSchemaMatcher: { uiType: 'SBDropdownWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            // console.log('DROPDOWN');
            // console.log(uiSchema);
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              required: schema.validators ? schema.validators.split('|').includes('required') : false,
              schema,
              // 'ui-schema': uiSchema,
            };
          },
        },
        {
          componentName: 'NMBRButtonsWrapper',
          uiSchemaMatcher: { uiType: 'NMBRButtonsWrapper' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'DividerWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },
          matcher: { type: 'dividerFull' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              schema,
            };
          },
        },
        {
          componentName: 'DividerWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },           // IMPORTANT: maybe uiSchema is not needed
          matcher: { type: 'divider' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': false,
              schema,
            };
          },
        },
        {
          componentName: 'SkeletonWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },           // IMPORTANT: maybe uiSchema is not needed
          matcher: { type: 'skeleton' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': false,
              schema,
            };
          },
        },
        {
          componentName: 'NMBRActionsWrapper',
          matcher: { type: 'action-buttons' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'FileInputWrapper',
          matcher: { type: 'file-input-wrapper' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'UrlInputWrapper',
          matcher: { type: 'url-input-wrapper' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: () => {},      // No need for a component since only the title is printed from the wrapper.
          matcher: { type: 'group-title' },
          props: (propName, schema, uiSchema) => {
            return {
              schema,
            };
          },
        },
        {
          componentName: 'CustomValidator',
          matcher: { type: 'custom-validator' },
          props: (propName, schema, uiSchema) => {
            return {
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'QuestionsWrapper',
          matcher: { type: 'questions' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'QuestionsListModalWrapper',
          matcher: { type: 'questions-list-modal' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
      ]
    };
  },
  computed: {
    wrapperComponentConfig() {
      return {
        componentName: 'SBCustomWrapperJSONForms',
        props: (propName, schema, uiSchema) => ({
          title: 'asd' || schema.title || schema.title === '' ? schema.title : propName,
          required: schema.validators ? schema.validators.split('|').includes('required') : false,
          disabled: uiSchema && uiSchema.disabled,
          errorMessage: schema.errorMessage,
          vertical: schema.type === 'object' || schema.type === 'array',
          schema,
          propName,
          customErrorMessage: this.customErrorMessage,
        }),
      };
    },
    schema() {
      // console.log('schemaBlueprint PROC', schemaBP);
      // const schema = { ...schemaBP };

      let schema = this.value ? this.filterSchema(JSON.parse(JSON.stringify(schemaBP))) : {};

      schema = this.filterCustomValidator(schema);

      // console.log('schema PROC', schema);
      return schema;
      // return {
      //   type: 'object',
      //   properties: {
      //     test: {
      //       type: 'input',
      //       title: 'Test',
      //       placeholder: 'Test',
      //       validators: 'required',
      //       subtitlePrefixIcon: SBIconList.INFO,
      //       subtitle: 'Test subtitle.',
      //     },
      //   },
      // };
    },
    /** Schema needs to be proccesed because some inputs can take less than 100% width and according to the number of elements in a row the appropriate  */
    // proccessedSchema() {
    //   return this.schema;
    // },
    uiSchema() {
      const uiSchema = {
        properties: {
          // b: {
          //   properties: {
          //     c: {
          //       uiType: 'BInputWrapper',
          //     },
          //     d: {
          //       uiType: 'SBDropdownWrapper',
          //     },
          //   },
          // },
        },
      };
      // Object.keys(this.schema.properties).forEach((prop) => {
        // switch (this.schema.properties[prop].type) {
        //   case 'input':
        //     console.log('input');
        //     console.log(prop);
        //     uiSchema.properties[prop] = {
        //       uiType: 'BInputWrapper',
        //     };
        //     break;
        //   case 'textarea':
        //     uiSchema.properties[prop] = {
        //       uiType: 'SBTextareaWrapper',
        //     };
        //     break;
        //   case 'dropdown':
        //     uiSchema.properties[prop] = {
        //       uiType: 'SBDropdownWrapper',
        //     };
        //     break;
        //   case 'message-buttons':
        //     uiSchema.properties[prop] = {
        //       uiType: 'NMBRButtonsWrapper',
        //     };
        //     break;
        //   case 'number':
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'b-input',
        //     // };
        //     break;
        //   case 'boolean':
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'b-input',
        //     // };
        //     break;
        //   case 'subform':
        //     uiSchema.properties[prop] = {
              
        //     };
        //     break;
        //   default:
        //     console.log('default');
        //     console.log(prop);
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'BInputWrapper',
        //     // };
        //     break;
        // }
      // });
      uiSchema.properties = {
        ...uiSchema.properties,
        ...this.proccessUiSchema(this.schema).properties,
      };
      // console.log('uiSchema', uiSchema);
      return uiSchema;
    },
  },
  watch: {
    value: {
      handler() {
        // console.log('checkData valid', (!this.$refs.jsonSchema.$v.$invalid).toString());
        // set custom validator messages
        this.runCustomValidators(this.value);
        this.$nextTick(() => {
          this.checkData();
        });
      },
      deep: true,
      immediate: false,
    },
  },
  updated() {
    // console.log('READY2');
    this.focusOnInit();
  },
  mounted() {
    // setTimeout(() => {
    //   // console.log('SHOWWW');
    //   this.showShema = true;
    // }, 5000);

    // setTimeout(() => {
    //   this.test7 = true;

    //   // console.dir(schemaBP);
    //   // console.dir(this.schema);
    // }, 10000);

    window.eventBus.$on('assistantStepsInit', this.stepInit);
  },
  methods: {
    stepInit(data) {
      // console.log('Step initialized', data.step);
      this.initializedSteps.push(data.step);
      if (this.initializedSteps.length === this.steps) {
        this.focusFirstInput();
      }
    },
    focusOnInit: debounce(function f() {
      // console.log('FOCUS 2', this.initialized);
      if (!this.initialized) {
        window.eventBus.$emit('assistantStepsInit', { step: 2 });
        this.initialized = true;
      }
    }, 100),
    focusFirstInput() {
      if (this.currentStep === 2) {
        // console.log('FOCUS -------');
        // Find first input field and focus it
        const input = document.getElementById('assistant-step-2').querySelector('input, .ql-editor');
        // console.log('input', input);
        if (input) input.focus();
      }
    },
    checkValue(condition = 'one', check = [], parentKey = '') {
      // If all the check fields are not dirty then return true
      const hasDirtyChecks = check.some((c) => {
        const targetKey = parentKey ? `${parentKey}.${c}` : c;
        const cField = this.getObjectCopyByPath(this.value, targetKey);
        return cField && cField.dirty;
      });
      if (!hasDirtyChecks) {
        return true;
      }

      let pass = false;
      // console.log('bla');

      // const targetKey = parentKey ? `${parentKey}.${input}` : input;
      const checks = {};

      check.forEach((c) => {
        // checks[c] = this.getObjectCopyByPath(this.value, c);
        const targetKey = parentKey ? `${parentKey}.${c}` : c;
        checks[c] = this.getObjectCopyByPath(this.value, targetKey);
        // if (
        //   !targetValue ||
        //   !targetValue.data ||
        //   (
        //     targetValue.data.value !== value &&
        //     (!Array.isArray(value) || !value.includes(targetValue.data.value))
        //   )
        // ) {
        //   delete schema.properties[key];
        //   delete this.value[key];                         // TODO : valueKey - FIX THIS to use path
        // } else {
        //   schema.properties[key].color = targetValue.data.color;
        // }
      });

      if (condition === 'one') {
        Object.keys(checks).some((c) => {
          if (checks[c] && checks[c].hasData && checks[c].valid) {
            // console.log('checks SET to empty');
            pass = true;
            return true;
          }
          // console.log('checks false');
          return false;
        });
        // return check.some((field) => {
        //   if (this.value[field] && this.value[field].data) {
        //     return this.value[field].data.value;
        //   }
        //   return false;
        // });
      }

      // console.log('checks message', pass);
      return pass;
    },
    /**
     * Special method for custom validator to remove it from schema if not needed.
     * Fields tested with custom validator must have a 'dirty' value in the data object.
     */
    filterCustomValidator(schema, parentKey = '') {
      if (!schema.properties) return schema;
      Object.keys(schema.properties).forEach((key) => {
        const valueKey = parentKey ? `${parentKey}.${key}` : key;
        const prop = schema.properties[key];
        if (prop.type === 'custom-validator') {
          prop.value = this.checkValue(prop.condition, prop.check, parentKey) ? '' : prop.message;


          // console.log('checks - ', prop.value, !prop.value);
          if (
            !prop.value
          ) {
            // console.group('checks DELETE PROC');
            // console.log(targetValue);
            // console.log(targetValue, value);
            // console.groupEnd();
            delete schema.properties[key];
            // console.log('DELETE', key);
            // console.log('parentKey', parentKey);
            if (parentKey) {
              const targetValue = this.getObjectCopyByPath(this.value, parentKey);
              // console.log('targetValue', targetValue);
              if (targetValue) {
                delete targetValue[key];
              }
              this.value[parentKey] = targetValue;
            } else {
              // console.log('NO parentKey', this.value);
              delete this.value[key];
            }
            
            // delete this.value[key];                         // TODO : valueKey - FIX THIS to use path
          }
        }
        if (schema.properties && schema.properties[key] && prop.type === 'object') {
          schema.properties[key] = this.filterCustomValidator(prop, valueKey);
        }
      });
      return schema;
    },
    filterSchema(schema, parentKey = '') {
      // console.log('---------- filterSchema', schema);
      // console.log('filterSchema', schema.properties);
      // const filteredSchema = {
      //   ...schema,
      //   properties: {},
      // };
      // const schemaCopy = { ...schema };
      Object.keys(schema.properties).forEach((key) => {
        const valueKey = parentKey ? `${parentKey}.${key}` : key;
        const prop = schema.properties[key];
        if (prop.showIf) {
          const { input, value } = prop.showIf;
          const targetKey = parentKey ? `${parentKey}.${input}` : input;
          const targetValue = getObjectCopyByPath(this.value, targetKey);
          // console.log('test PROC', targetValue, value);
          // console.log('test PROC 2', targetValue, value);
          if (
            !targetValue ||
            !targetValue.data ||
            (
              targetValue.data.value !== value &&
              (!Array.isArray(value) || !value.includes(targetValue.data.value))
            )
          ) {
            // console.group('DELETE PROC');
            // console.log(targetValue);
            // console.log(targetValue, value);
            // console.groupEnd();
            delete schema.properties[key];
            delete this.value[key];                         // TODO : valueKey - FIX THIS to use path
          } else if (!schema.properties[key].color && targetValue.data.color) {
            // const controlField = this.findFieldInSchema(schemaBP, input);
            // console.log('controlField PROC', this.value[input].data.color);
            schema.properties[key].color = targetValue.data.color;
          } else if (schema.properties[key].color && schema.properties[key].color === 'default') {
            delete schema.properties[key].color;
          }
        }
        // if (prop.type === 'custom-validator' && !prop.value) {
        //   // console.log('HERE');
        //   delete schema.properties[key];
        //   delete this.value[key];
        // }
        if (schema.properties && schema.properties[key] && prop.type === 'object') {
          schema.properties[key] = this.filterSchema(prop, valueKey);
        }
      });
      return schema;
    },
    findFieldInSchema(schema, key) {
      if (schema.properties && schema.properties[key]) {
        return schema.properties[key];
      }
      Object.keys(schema.properties).forEach((prop) => {
        if (schema.properties[prop].type === 'object') {
          return this.findFieldInSchema(schema.properties[prop], key);
        }
      });
      return null;
    },
    proccessUiSchema(schema) {
      const uiSchema = {
        properties: {},
      };
      Object.keys(schema.properties).forEach((prop) => {
        switch (schema.properties[prop].type) {
          case 'input':
            // console.log('input');
            // console.log(prop);
            uiSchema.properties[prop] = {
              uiType: 'BInputWrapper',
            };
            break;
          case 'textarea':
            uiSchema.properties[prop] = {
              uiType: 'SBTextareaWrapper',
            };
            break;
          case 'dropdown':
            uiSchema.properties[prop] = {
              uiType: 'SBDropdownWrapper',
            };
            break;
          case 'message-buttons':
            uiSchema.properties[prop] = {
              uiType: 'NMBRButtonsWrapper',
            };
            break;
          // case 'dividerFull':
          //   uiSchema.properties[prop] = {
          //     uiType: 'DividerWrapper',
          //   };
          //   break;
          case 'number':
            // uiSchema.properties[prop] = {
            //   uiType: 'b-input',
            // };
            break;
          case 'boolean':
            // uiSchema.properties[prop] = {
            //   uiType: 'b-input',
            // };
            break;
          case 'object':
            // uiSchema.properties[prop] = {
            uiSchema.properties[prop] = {
              properties: {
                ...this.proccessUiSchema(schema.properties[prop]).properties,
              },
            };
            // };
            break;
          default:
            // console.log('default');
            // console.log(prop);
            // uiSchema.properties[prop] = {
            //   uiType: 'BInputWrapper',
            // };
            break;
        }
      });
      // console.log('returning uiSchema', uiSchema);
      return uiSchema;
    },
    runCustomValidators(form) {
      Object.keys(form).forEach((component) => {
        if (form[component].validOneOf) {
          // check all the values in the validOneOf array
          const oneIsValid = form[component].validOneOf.some(v => form[v] && form[v].valid);
          const dirty = form[component].validOneOf.some(v => form[v] && form[v].dirty);
          if (!oneIsValid) {
            if (form[component].validOneOfMessage) {
              this.customErrorMessage[component] = {
                message: form[component].validOneOfMessage,
                dirty,
              };
            }
          } else {
            // must delete all of them
            Object.keys(this.customErrorMessage).forEach((key) => {
              if (form[key].validOneOf.includes(key)) {
                delete this.customErrorMessage[key];
              }
            });
          }
        }
        if (form[component].valid === undefined) {
          this.runCustomValidators(form[component]);
        }
      });
    },
    checkFormValid(form) {
      return !Object.keys(form).some((component) => {
        // First check if it has 'Value' key. If it doesn't have it then this is a subform and it needs to be passed to the checkData function recursively.
        if (!form[component] || this.emptyObject(form[component])) return true;
        if (form[component].validOneOf) {
          return !!this.customErrorMessage[component];
        }
        if (form[component].valid === undefined) {
          if (!this.checkFormValid(form[component])) {
            return true;
          }
          return false;
        }
        if (form[component] && form[component].valid === false) {
          return true;
        }
        return false;
      });
    },
    checkData() {
      const formValid = this.checkFormValid(this.value);
      this.$emit('input', {
        valid: formValid,
        name: 'assistantStep1',
        data: this.value,
      });
    },
    initDefault(v) {
      this.value = v;
    },
  },
};
</script>

<style scoped>
h1 {
  color: #42b983;
}
</style>
